// Default crew for a production event type, from the global scheduling rules
// (settings/schedulingRules, edited in AdminSchedulingRules.vue).
//
// Applied at event CREATION only — never on update, or an admin who
// deliberately cleared the crew would see it reappear. Every creation path
// (ProjectCalendarManager, BulkAddProductionDaysModal, clientCalendarStore,
// episodeTemplateService) seeds assignedUserIds through this helper so they
// can't disagree.
//
// Knock-on: functions/googleCalendar/staffSyncTrigger.js turns assignedUserIds
// into Google attendees (admin/super only), so a prefilled crew is invited as
// soon as the event exists.

import { DEFAULT_SCHEDULING_RULES } from '@/constants/schedulingRules';

/**
 * @param {string} type - A calendar EVENT_TYPES value
 * @param {Object} [rules] - The scheduling ruleset (schedulingRulesStore.rules)
 * @returns {string[]} A fresh array of user ids (safe to mutate)
 */
export const resolveDefaultAssignees = (type, rules = DEFAULT_SCHEDULING_RULES) => {
  const ids = rules?.assigneeDefaults?.[type];
  return Array.isArray(ids) ? [...new Set(ids.filter(Boolean))] : [];
};

/**
 * Fill an event's crew from the type's default, and ONLY when the caller
 * didn't speak for it.
 *
 * The test is "is the field absent", NOT "is it empty". An explicit `[]` is a
 * decision — the bulk modal seeds its picker from these same defaults, so an
 * admin who removes every name before saving is saying "no crew", and refilling
 * it would both ignore them and fire Google invites they didn't ask for.
 * Creation paths that never mention assignedUserIds get the default; paths that
 * set it, even to empty, are left alone.
 *
 * Returns the same object when there is nothing to add, so callers doing
 * change detection don't see a phantom edit.
 *
 * @param {Object} eventData - An event about to be created
 * @param {Object} [rules]
 * @returns {Object}
 */
export const withDefaultAssignees = (eventData, rules = DEFAULT_SCHEDULING_RULES) => {
  if (!eventData || eventData.assignedUserIds !== undefined) return eventData;
  // Podcast sessions (showId) get their crew from podcastCrewTrigger instead.
  if (eventData.showId) return eventData;
  const defaults = resolveDefaultAssignees(eventData.type, rules);
  return defaults.length ? { ...eventData, assignedUserIds: defaults } : eventData;
};
